import { normalizeEmployeeLogin } from "@/lib/auth";
import {
  isPublicPendingAbsenceType,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import type { UnavailableOnDayPerson } from "@/lib/montageplanung";

export type TeamTodayEntry =
  | { source: "absence"; req: EmployeeAbsenceRequest }
  | { source: "unavailable"; person: UnavailableOnDayPerson };

function isVisibleAbsence(req: EmployeeAbsenceRequest): boolean {
  if (req.absence_type === "nicht_verfuegbar") return false;
  if (req.status === "approved") return true;
  return req.status === "pending" && isPublicPendingAbsenceType(req.absence_type);
}

function coversDay(fromIso: string, toIso: string, day: string): boolean {
  const from = fromIso.slice(0, 10);
  const to = toIso.slice(0, 10);
  return from <= day && to >= day;
}

function personName(login: string, display: string | null | undefined): string {
  return (display || login || "").trim();
}

/**
 * Heute: genehmigte bzw. öffentliche Abwesenheiten plus Montageplan „Nicht verfügbar“.
 * Demnächst: nur Abwesenheiten, die nach heute beginnen. Nicht-Verfügbarkeit bleibt dort draußen.
 */
export function splitTeamAbsences(input: {
  absences: EmployeeAbsenceRequest[];
  unavailableToday: UnavailableOnDayPerson[];
  today: string;
  horizon: string;
}): { todayRows: TeamTodayEntry[]; upcomingRows: EmployeeAbsenceRequest[] } {
  const today = input.today.slice(0, 10);
  const horizon = input.horizon.slice(0, 10);
  const visible = input.absences.filter(isVisibleAbsence);
  const todayAbsences = visible
    .filter((req) => coversDay(req.date_from, req.date_to, today))
    .sort((a, b) =>
      personName(a.user_login, a.user_display).localeCompare(
        personName(b.user_login, b.user_display),
        "de",
      ),
    );
  const absentLogins = new Set(
    todayAbsences
      .map((req) => normalizeEmployeeLogin(req.user_login))
      .filter(Boolean),
  );
  const unavailable = input.unavailableToday
    .filter((person) => coversDay(person.date_from, person.date_to, today))
    .filter((person) => {
      const login = normalizeEmployeeLogin(person.user_login);
      return !login || !absentLogins.has(login);
    })
    .map(
      (person): TeamTodayEntry => ({
        source: "unavailable",
        person,
      }),
    );
  const todayRows: TeamTodayEntry[] = [
    ...todayAbsences.map((req): TeamTodayEntry => ({ source: "absence", req })),
    ...unavailable,
  ].sort((a, b) => displayOf(a).localeCompare(displayOf(b), "de"));

  const upcomingRows = visible
    .filter((req) => {
      const from = req.date_from.slice(0, 10);
      return from > today && from <= horizon;
    })
    .sort((a, b) => a.date_from.localeCompare(b.date_from));

  return { todayRows, upcomingRows };
}

function displayOf(entry: TeamTodayEntry): string {
  if (entry.source === "absence") {
    return personName(entry.req.user_login, entry.req.user_display);
  }
  return personName(entry.person.user_login, entry.person.user_display);
}
